import { HttpContext } from "@angular/common/http";
import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn } from "@angular/router";
import { catchError, map, Observable, of } from "rxjs";
import { pathParamsHelper } from "../helpers/path-params.helper";
import { DefaultProduct } from "../pia-api/generated";
import { PiaContextService } from "../pia-api/pia-context.service";
import { PiaProductsService } from "../pia-api/pia-products.service";
import { IS_IN_RESOLVER_CONTEXT } from "../tokens/resolver-context.token";
import { withLoading } from "../utils/with-loading";

/**
 * used to get the product families, which is the first level in the product hierarchy (product family, product range, product series, product detail),
 * the data acts as base for the filters for example
 * @param route
 */
export const productFamiliesResolver: ResolveFn<
  Observable<DefaultProduct[] | null>
> = withLoading((route: ActivatedRouteSnapshot) => {
  const piaProductsService = inject(PiaProductsService);

  const countryID = route?.paramMap?.get("country")?.toUpperCase() as string;
  const languageID = route?.paramMap?.get("language")?.toLowerCase() as string;
  const year = Number(route?.paramMap?.get("year") as string);

  if (!languageID || !countryID || isNaN(year)) {
    return of(null);
  }
  const context = new HttpContext().set(IS_IN_RESOLVER_CONTEXT, true);

  return piaProductsService
    .getProductFamiliesByYearAndCountryAndLanguage(
      {
        countryID,
        languageID,
        year,
      },
      context,
    )
    .pipe(
      map((data) => {
        return data;
      }),
      catchError(() => {
        return of(null);
      }),
    );
});

/**
 * used to get the available product family years for a country and language combination
 * @param route
 */
export const productFamiliesYearsResolver: ResolveFn<
  Observable<number[] | null>
> = withLoading((route: ActivatedRouteSnapshot) => {
  const contextService = inject(PiaContextService);

  const { countryID, languageID, year } = pathParamsHelper(route);
  const context = new HttpContext().set(IS_IN_RESOLVER_CONTEXT, true);

  if (!languageID || !countryID || isNaN(year)) {
    return of(null);
  }

  return contextService
    .getYearsByCountryIdAndLanguageId(
      {
        countryID,
        languageID,
      },
      context,
    )
    .pipe(
      map((data) => {
        if (data?.length && data.includes(year)) {
          return data;
        } else {
          return null;
        }
      }),
      catchError(() => {
        return of(null);
      }),
    );
});

/**
 * used to get the product family year from the route path
 * @param route
 */
export const productFamiliesPathYearResolver: ResolveFn<number | null> = (
  route: ActivatedRouteSnapshot,
) => {
  const yearParam = route?.paramMap?.get("year") as string;

  if (!yearParam || isNaN(parseInt(yearParam, 10))) {
    return null;
  }

  return parseInt(yearParam, 10);
};
